import { useState, useEffect } from "react";

import PropTypes from "prop-types";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import InputCRM from "components/RH/inputs/InputCRM"; // Certifique-se que o caminho está correto
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import InputDate from "components/RH/inputs/InputDate";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskEnum from "utils/MaskEnum";
import MaskUtil from "utils/MaskUtil";

const Afastamento = ({ id }) => {
  const { setLoading } = useLoading();
  const auth = useAuth();
  const Requicicao = new Api();
  const notify = useNotification();

  const { MotivoDesligamentoOptions, AvisoPrevioOptions, StatusFuncionario } =
    MaskEnum;

  const [Dados, setDados] = useState({});
  const [TipoAfastamento, setTipoAfastamento] = useState("");

  const [Lista, setLista] = useState([]);
  const [Arquivo, setArquivo] = useState(null);

  const [ShowForm, setShowForm] = useState(false);

  const HandleDropdownChange = (e, name) => {
    setDados((prevState) => ({
      ...prevState,
      [name]: e.value
    }));
  };

  const HandleInputChange = (e) => {
    const { name, value } = e.target;
    setDados((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const HandleFileSelect = (e) => {
    setArquivo(e.files[0]); // Captura o primeiro arquivo selecionado
  };

  const HandleDownloadDocumento = async (nomeArquivo, tipo) => {
    try {
      setLoading(true);
      const response = await Requicicao.Get({
        endpoint: `/Funcionarios/${id}/${tipo}`,
        params: { nomeArquivo },
        config: {
          ...auth.GetHeaders(),
          responseType: "blob"
        }
      });

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (error) {
      console.error("Erro ao baixar documento:", error);
      notify({
        type: "error",
        message: "Erro ao baixar documento. Tente novamente mais tarde."
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const SubirArquivo = async (statusFuncionarioId) => {
    try {
      setLoading(true);

      const formData = new FormData();

      const dataToForm = {
        funcionarioId: id,
        nomeArquivo: `${id}_${MaskUtil.applyDataAndHoraMask(new Date())}`,
        descricao: StatusFuncionario.find((x) => x.value === 4).label,
        tipoArquivo: 3,
        statusFuncionarioId
      };

      // Adiciona os dados do funcionário ao FormData
      for (const key in dataToForm) {
        if (Object.prototype.hasOwnProperty.call(dataToForm, key)) {
          formData.append(key, dataToForm[key]);
        }
      }

      // Adiciona o arquivo ao FormData
      if (Arquivo) {
        formData.append("arquivoFuncionario", Arquivo); // 'arquivo' deve ser o nome esperado pelo servidor

        await Requicicao.Put({
          endpoint: `/Funcionarios/AdicionarArquivosFuncionario`,
          data: formData,
          config: auth.GetHeaders("multipart/form-data")
        });

        notify({
          type: "sucesso",
          message: "Documento Salvo"
        });
      } else {
        notify({
          type: "aviso",
          message: "Por favor, selecione um arquivo para fazer upload."
        });
      }
    } catch (error) {
      console.error(error);
      notify({
        type: "erro",
        message: "Erro ao salva documento."
      });
    } finally {
      setLoading(false);
    }
  };

  const GetHistorico = async () => {
    try {
      setLoading(true);
      const data = await Requicicao.Get({
        endpoint: "/Funcionarios/Status",
        params: { id },
        config: auth.GetHeaders()
      });
      if (data.codigoEstadoHttp === 200) {
        setLista(
          data.objetoResposta.sort((a, b) => {
            // Pega a data de atualização ou data de criação, se a atualização for null
            const dataA = new Date(a.dataAtualizacao || a.dataCriacao);
            const dataB = new Date(b.dataAtualizacao || b.dataCriacao);
            return dataB - dataA;
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const ValidarCamposObrigatorios = () => {
    const erros = [];

    // Validação básica para todos os tipos de afastamento
    if (!TipoAfastamento) {
      erros.push("Selecione o tipo de afastamento primeiro");
    }

    if (TipoAfastamento && !Dados.dataInicio) {
      erros.push("Data de Início é obrigatória");
    }

    // Validações específicas para afastamento médico (status 4)
    if (TipoAfastamento === 4) {
      if (!Dados.nomeMedico || Dados.nomeMedico.trim() === "") {
        erros.push("Nome do Médico é obrigatório");
      }
      // CRM é validado pelo próprio componente InputCRM
      if (!Arquivo) {
        erros.push("Atestado Médico é obrigatório");
      }
    }



    // Validação de data de término para afastamentos temporários
    if (TipoAfastamento && !Dados.dataFim) {
      erros.push("Data de Término é obrigatória para afastamentos temporários");
    }

    // Validação de descrição
    if (!Dados.descricao || Dados.descricao.trim() === "") {
      erros.push("Anotação é obrigatória");
    }

    return erros;
  };

  const Submit = async (params, payload) => {
    if (params != null) params.preventDefault();
    
    // Validação dos campos obrigatórios
    const erros = ValidarCamposObrigatorios();
    if (erros.length > 0) {
      notify({
        type: "error",
        message: `Por favor, preencha os seguintes campos obrigatórios:<br/>• ${erros.join("<br/>• ")}`
      });
      return;
    }

    try {
      setLoading(true);

      const Payload = Dados;
      Payload.status = TipoAfastamento;
      if (Dados.dataInicio && Dados.dataInicio.trim() !== "")
        Payload.dataInicio = MaskUtil.convertDateToISO(Dados.dataInicio);
      if (Dados.dataFim && Dados.dataFim.trim() !== "")
        Payload.dataFim = MaskUtil.convertDateToISO(Dados.dataFim);

      const data = await Requicicao.Post({
        endpoint: "/Funcionarios/Status",
        data: payload ?? Payload,
        config: auth.GetHeaders()
      });

      if (data.codigoEstadoHttp === 200) {
        notify({
          type: "sucesso",
          message: "Status Salvo"
        });
        setDados({ funcionarioId: id });
        setTipoAfastamento("");
        if (Dados.status === 4) await SubirArquivo(data.objetoResposta.id);
        setShowForm(false);
        await GetHistorico();
      } else if (data.mensagemAdicional) {
        notify({
          type: "erro",
          message: data.mensagemAdicional
        });
      }
    } catch (error) {
      console.error(error);
      notify({
        type: "erro",
        message: "Erro ao salvar afastamento"
      });
    } finally {
      setLoading(false);
    }
  };

  const Ativar = async (e) => {
    try {
      setLoading(true);
      
      const payload = {
        funcionarioId: id,
        status: 1,
        dataInicio: new Date(),
        descricao: "Funcionário retornou ao trabalho"
      };

      const data = await Requicicao.Post({
        endpoint: "/Funcionarios/Status",
        data: payload,
        config: auth.GetHeaders()
      });

      if (data.codigoEstadoHttp === 200) {
        notify({
          type: "sucesso",
          message: "Funcionário ativado com sucesso"
        });
        await GetHistorico();
      } else if (data.mensagemAdicional) {
        notify({
          type: "erro",
          message: data.mensagemAdicional
        });
      }
    } catch (error) {
      console.error(error);
      notify({
        type: "erro",
        message: "Erro ao ativar funcionário"
      });
    } finally {
      setLoading(false);
    }
  };



  const RemoverStatus = async (statusId) => {
    try {
      setLoading(true);
      await Requicicao.Delete({
        endpoint: "/Funcionarios/Status",
        params: { id: statusId },
        config: auth.GetHeaders()
      });
      await GetHistorico();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };



  const ConfirmacaoAtivar = (event) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Tem certeza que quero ativo esse funcionário agora?",
      icon: "pi pi-info-circle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => Ativar(event),
      reject: () => {}
    });
  };

  const ConfirmacaoRemover = (event, statusId) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Tem certeza que deseja excluir este afastamento?",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim, excluir",
      rejectLabel: "Cancelar",
      accept: () => RemoverStatus(statusId),
      reject: () => {}
    });
  };

  useEffect(() => {
    setDados((e) => ({ ...e, funcionarioId: id }));
    // Carrega o histórico automaticamente quando o componente é montado
    GetHistorico();
  }, [id]);

  return (
    <div>
      <ConfirmPopup />
      
      {/* Cabeçalho explicativo */}
      <div className="alert alert-info mb-4">
        <div className="d-flex align-items-center">
          <i className="pi pi-info-circle me-3" style={{ fontSize: '1.5rem' }} />
          <div>
            <h6 className="mb-1">Gestão de Afastamentos</h6>
            <p className="mb-0 small">
              Use esta aba para registrar afastamentos temporários (férias, doença, etc.) e marcar quando o funcionário retornou ao trabalho.
            </p>
          </div>
        </div>
      </div>
      {ShowForm ? (
        <form className="row g-3" onSubmit={Submit}>
          <div className="col-md-4">
            <label htmlFor="tipoAfastamento" className="form-label">
              Tipo de Afastamento <span className="text-danger">*</span>
            </label>
            <Dropdown
              id="tipoAfastamento"
              value={TipoAfastamento}
              onChange={(e) => setTipoAfastamento(e.value)}
              options={StatusFuncionario.filter(
                (x) => x.value !== 1 && x.value !== 2 && x.value !== 0 && x.value !== 3
              )}
              className={`w-100 ${!TipoAfastamento ? 'p-invalid' : ''}`}
            />
            {!TipoAfastamento && (
              <small className="text-danger">Selecione o tipo de afastamento primeiro</small>
            )}
          </div>

          {TipoAfastamento && (
            <div className="col-md-4">
              <label htmlFor="data-inicio" className="form-label">
                Data de Início <span className="text-danger">*</span>
              </label>
              <InputDate
                id="dataInicio"
                name="dataInicio"
                value={Dados.dataInicio}
                onChange={(dateString) => HandleInputChange({ target: { name: "dataInicio", value: dateString } })}
                placeholder="dd/mm/aaaa"
                className={`w-100 ${!Dados.dataInicio ? 'p-invalid' : ''}`}
              />
              {!Dados.dataInicio && (
                <small className="text-danger">Data de Início é obrigatória</small>
              )}
            </div>
          )}

                                                {TipoAfastamento && (
                <>
                  <div className="col-md-4">
                      <label htmlFor="data-termino" className="form-label">
                        Data de Término <span className="text-danger">*</span>
                      </label>
                      <InputDate
                        id="dataFim"
                        name="dataFim"
                        value={Dados.dataFim}
                        onChange={(dateString) => HandleInputChange({ target: { name: "dataFim", value: dateString } })}
                        placeholder="dd/mm/aaaa"
                        className={`w-100 ${!Dados.dataFim ? 'p-invalid' : ''}`}
                      />
                      {!Dados.dataFim && (
                        <small className="text-danger">Data de Término é obrigatória para afastamentos temporários</small>
                      )}
                    </div>

              {TipoAfastamento === 4 && (
                <>
                  {/* <div className="col-md-4">
                  <label htmlFor="atestado" className="form-label">
                    Atestado Médico de Afastamento
                  </label>
                  <InputText
                    id="atestado"
                    name="atestado"
                    value={Dados.atestado}
                    onChange={HandleInputChange}
                    className="w-100"
                  />
                </div> */}
                  <div className="col-md-4">
                    <label htmlFor="nomeMedico" className="form-label">
                      Nome do Médico <span className="text-danger">*</span>
                    </label>
                    <InputText
                      id="nomeMedico"
                      name="nomeMedico"
                      value={Dados.nomeMedico}
                      onChange={HandleInputChange}
                      className={`w-100 ${(!Dados.nomeMedico || Dados.nomeMedico.trim() === "") ? 'p-invalid' : ''}`}
                    />
                    {(!Dados.nomeMedico || Dados.nomeMedico.trim() === "") && (
                      <small className="text-danger">Nome do Médico é obrigatório</small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="crm" className="form-label">
                      CRM <span className="text-danger">*</span>
                    </label>
                    <InputCRM
                      id="crm"
                      name="crm"
                      value={Dados.crm}
                      onChange={HandleInputChange}
                      className="form-control"
                      placeholder="123456/SP"
                    />
                  </div>
                  <div className="col-md-12">
                    <label htmlFor="atestado" className="form-label">
                      Atestado Médico de Afastamento <span className="text-danger">*</span>
                    </label>
                    <FileUpload
                      chooseLabel="Escolher"
                      uploadLabel="Enviar"
                      cancelLabel="Cancelar"
                      accept="application/pdf"
                      emptyTemplate={
                        <p className="m-0">
                          Arraste e solte os arquivos aqui para fazer upload.
                        </p>
                      }
                      customUpload
                      onSelect={HandleFileSelect}
                      className={!Arquivo ? 'p-invalid' : ''}
                    />
                    {!Arquivo && (
                      <small className="text-danger">Atestado Médico é obrigatório</small>
                    )}
                  </div>
                </>
              )}



              <div className="col-md-12">
                <label htmlFor="descricao" className="form-label">
                  Anotação <span className="text-danger">*</span>
                </label>
                <InputTextarea
                  id="descricao"
                  name="descricao"
                  value={Dados.descricao}
                  onChange={HandleInputChange}
                  className={`w-100 ${(!Dados.descricao || Dados.descricao.trim() === "") ? 'p-invalid' : ''}`}
                />
                {(!Dados.descricao || Dados.descricao.trim() === "") && (
                  <small className="text-danger">Anotação é obrigatória</small>
                )}
              </div>
            </>
          )}

          <div className="col-md-12">
            <div className="d-flex flex-row-reverse gap-3">
              <Button
                className="btn btn-primary"
                type="submit"
                label="Atualizar"
              />
              <Button
                type="button"
                label="Cancelar"
                className="btn"
                onClick={() => setShowForm(false)}
              />
            </div>
          </div>
        </form>
      ) : (
        <div className="d-flex flex-row-reverse gap-3">
          <Button
            className="btn btn-primary"
            type="submit"
            label="Novo Afastamento"
            icon="pi pi-plus"
            onClick={() => setShowForm(true)}
          />
          <Button
            type="button"
            label="Retornou ao Trabalho"
            severity="success"
            icon="pi pi-check-circle"
            tooltip="Marca que o funcionário retornou ao trabalho"
            tooltipOptions={{ position: "bottom" }}
            onClick={ConfirmacaoAtivar}
          />
        </div>
      )}

      {Lista.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              <i className="pi pi-history me-2" />
              Histórico de Afastamentos
            </h6>
            <span className="badge bg-primary">{Lista.length} registro(s)</span>
          </div>
          <DataTable value={Lista} stripedRows paginator rows={10}>
            <Column
              field="status"
              header="Status"
              body={(row) =>
                StatusFuncionario.find((x) => x.value === row.status)?.label ?? "N/A"
              }
            />
            <Column field="descricao" header="Descrição" />
            <Column
              field="nomeMedico"
              header="Nome do Médico"
              body={(row) => row.nomeMedico || "N/A"}
            />
            <Column
              field="crm"
              header="CRM"
              body={(row) => row.crm || "N/A"}
            />
            <Column
              field="dataInicio"
              header="Data de Início"
              body={(row) => MaskUtil.applyDataMask(row.dataInicio)}
            />
            <Column
              field="dataFim"
              header="Data de Término"
              body={(row) => MaskUtil.applyDataMask(row.dataFim)}
            />
            <Column
              field="dataCriacao"
              header="Data de Criação"
              body={(row) => MaskUtil.applyDataAndHoraMask(row.dataCriacao)}
            />
            <Column
              field="nomeTipoArquivo"
              header="Tipo de Arquivo"
              body={(row) => (
                <div className="d-flex align-items-center gap-2">
                  <span>
                    {row.nomeTipoArquivo || "N/A"}
                  </span>
                  {row.nomeArquivo && (
                    <Button
                      type="button"
                      icon="pi pi-download"
                      className="p-button-text p-button-sm"
                      tooltip="Baixar documento"
                      tooltipOptions={{ position: "top" }}
                      onClick={() => HandleDownloadDocumento(row.nomeArquivo, "DownloadDoc")}
                    />
                  )}
                </div>
              )}
            />
            <Column
              header="Ação"
              body={(row) => (
                <Button
                  type="button"
                  className="btn btn-tabela"
                  icon="pi pi-trash"
                  onClick={(event) => ConfirmacaoRemover(event, row.id)}
                />
              )}
            />
          </DataTable>
        </div>
      )}
    </div>
  );
};

Afastamento.propTypes = {
  id: PropTypes.string.isRequired
};

export default Afastamento;
